import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { useTheme } from '../../../context/ThemeContext';
import { useAuth } from '../../../context/AuthContext';
import { supabase } from '../../../config/supabase';
import { redeemReward } from '../../superadmin/utils/referralService';

const PAGE_SIZE = 8;

export default function ReferralPage({ settings = {} }) {
  const { lang } = useLanguage();
  const { isDark } = useTheme();
  const { user, agencyId } = useAuth();
  const isAr = lang === 'ar';
  const t = (fr, ar) => (isAr ? ar : fr);

  const uid = agencyId || user?.id || user?.uid || '';
  const code = String(uid).split('-')[0]; // short referral code (8 chars)
  const link = `${window.location.origin}/?ref=${code}`;

  const [referrals, setReferrals] = useState([]);
  const [rewards, setRewards] = useState(settings.referralRewards || []);
  const [freeDaysTotal, setFreeDaysTotal] = useState(settings.referralFreeDaysTotal || 0);
  const [loading, setLoading] = useState(true);
  const [rpcError, setRpcError] = useState('');
  const [toast, setToast] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 2500); };

  const load = useCallback(async () => {
    try {
      // Referred agencies list (secure RPC). If the RPC isn't deployed yet, we
      // still show earnings from the agency's own row below.
      const { data, error } = await supabase.rpc('get_my_referrals');
      if (error) setRpcError(error.message || 'RPC indisponible');
      else { setRpcError(''); if (Array.isArray(data)) setReferrals(data); }

      // Earnings/commissions live on THIS agency's own row (RLS-isolated).
      if (uid) {
        const { data: me, error: meErr } = await supabase
          .from('user_registrations').select('data').eq('uid', uid).single();
        if (!meErr && me?.data) {
          setRewards(Array.isArray(me.data.referralRewards) ? me.data.referralRewards : []);
          setFreeDaysTotal(me.data.referralFreeDaysTotal || 0);
        }
      }
    } catch (e) { setRpcError(e?.message || 'Erreur de chargement'); }
    setLoading(false);
  }, [uid]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const statusOf = (r) => {
    const active = r.subEnd && new Date(r.subEnd) > new Date();
    if (active) return 'active';
    if (r.rewarded || r.hasPaid) return 'confirmed';
    return 'pending';
  };
  const freeDaysFor = (refUid) =>
    rewards.filter(x => x.referredUid === refUid).reduce((s, x) => s + (x.freeDays || 0), 0);

  const enriched = useMemo(() => referrals.map(r => ({ ...r, status: statusOf(r), freeDays: freeDaysFor(r.uid) })),
    [referrals, rewards]); // eslint-disable-line

  const stats = useMemo(() => ({
    total: enriched.length,
    valid: enriched.filter(r => r.status !== 'pending').length,
    pending: enriched.filter(r => r.status === 'pending').length,
    freeDays: freeDaysTotal,
  }), [enriched, freeDaysTotal]);

  const filtered = useMemo(() => {
    let list = enriched;
    if (filter !== 'all') list = list.filter(r => r.status === filter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(r => `${r.name || ''} ${r.email || ''}`.toLowerCase().includes(s));
    }
    return list;
  }, [enriched, filter, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { setPage(1); }, [filter, search]);

  const copy = (txt) => {
    try { navigator.clipboard.writeText(txt); notify(t('✅ Copié !', '✅ تم النسخ!')); }
    catch { notify(t('❌ Impossible de copier', '❌ تعذّر النسخ')); }
  };

  const pendingRewards = useMemo(() => rewards.filter(r => !r.redeemed), [rewards]);
  const cashTotal = useMemo(() =>
    rewards.filter(r => r.redeemed && r.redeemType === 'cash').reduce((s, r) => s + (r.cashAmount || 0), 0), [rewards]);

  // Commission earnings (a commission = one validated referral worth its cash value).
  const currency = useMemo(() => (rewards.find(r => r.currency)?.currency) || 'DH', [rewards]);
  const totalCommission = useMemo(() => rewards.reduce((s, r) => s + (r.cashAmount || 0), 0), [rewards]);
  const pendingValue = useMemo(() => pendingRewards.reduce((s, r) => s + (r.cashAmount || 0), 0), [pendingRewards]);
  // Referred-agency count: from the RPC list, or fall back to the rewards count.
  const referredCount = useMemo(() => Math.max(referrals.length, rewards.length), [referrals, rewards]);

  const redeem = async (referredUid, type) => {
    const msg = type === 'days'
      ? t('Convertir cette récompense en jours gratuits ?', 'تحويل هذه المكافأة إلى أيام مجانية؟')
      : t('Demander le retrait de cette récompense en argent ?', 'طلب سحب هذه المكافأة نقداً؟');
    if (!confirm(msg)) return;
    try {
      const res = await redeemReward({ supabase, uid, referredUid, type });
      if (res.ok) {
        notify(type === 'days'
          ? t('✅ Jours ajoutés à votre abonnement !', '✅ تمت إضافة الأيام لاشتراكك!')
          : t('✅ Demande de retrait envoyée à l\'administrateur.', '✅ تم إرسال طلب السحب للمدير.'));
        await load();
      } else { notify(t('❌ Récompense introuvable', '❌ المكافأة غير موجودة')); }
    } catch (e) { notify('❌ ' + e.message); }
  };

  const cardBg = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const sub = isDark ? 'text-gray-400' : 'text-gray-500';
  const txt = isDark ? 'text-white' : 'text-gray-900';

  const statusBadge = (st) => {
    const map = {
      active: ['bg-green-100 text-green-700', t('Active', 'نشط')],
      confirmed: ['bg-blue-100 text-blue-700', t('Confirmé', 'مؤكد')],
      pending: ['bg-amber-100 text-amber-700', t('En attente', 'قيد الانتظار')],
    };
    const [cls, label] = map[st] || map.pending;
    return <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="mb-5">
        <h1 className={`text-2xl font-black flex items-center gap-2 ${txt}`}>🎁 {t('Parrainage', 'الإحالة')}</h1>
        <p className={`text-sm mt-1 ${sub}`}>
          {t('Invitez d\'autres agences et gagnez des jours gratuits à chaque parrainage validé.',
             'ادعُ وكالات أخرى واربح أياماً مجانية عند كل إحالة مؤكدة.')}
        </p>
      </div>

      <div className={`rounded-2xl border p-4 mb-5 ${cardBg}`}>
        <div className={`text-[11px] font-bold uppercase tracking-wider mb-2 ${sub}`}>{t('Votre lien de parrainage', 'رابط الإحالة الخاص بك')}</div>
        <div className="flex gap-2 flex-wrap">
          <input readOnly value={link}
            className={`flex-1 min-w-[200px] px-3 py-2 rounded-lg border text-sm font-mono ${isDark ? 'bg-gray-900 border-gray-600 text-gray-200' : 'bg-gray-50 border-gray-200 text-gray-700'}`} />
          <button onClick={() => copy(link)} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold text-sm">
            📋 {t('Copier le lien', 'نسخ الرابط')}
          </button>
        </div>
        <div className={`mt-2 text-xs ${sub}`}>
          {t('Votre code', 'الرمز الخاص بك')}: <span className="font-mono font-bold">{code}</span>
          <button onClick={() => copy(code)} className="ml-2 text-indigo-500 hover:underline">{t('copier', 'نسخ')}</button>
        </div>
      </div>

      {/* How it works — explanatory banner */}
      <div className="rounded-2xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 p-4 mb-5">
        <div className="font-black text-indigo-800 dark:text-indigo-300 mb-1">💡 {t('Comment ça marche', 'كيف يعمل')}</div>
        <p className="text-sm text-indigo-700 dark:text-indigo-200 leading-relaxed">
          {t('Pour chaque agence qui s\'inscrit avec votre lien ET paie son premier abonnement, vous gagnez une récompense. À vous de choisir :',
             'مقابل كل وكالة تسجّل عبر رابطك وتدفع أول اشتراك لها، تربح مكافأة. الاختيار لك:')}
        </p>
        <div className="flex flex-wrap gap-3 mt-2 text-sm">
          <span className="px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-indigo-200 dark:border-indigo-700 font-bold text-indigo-700 dark:text-indigo-300">
            🎁 {t('Convertir en jours gratuits', 'تحويلها إلى أيام مجانية')} → {t('utilisez la plateforme gratuitement', 'استخدم المنصة مجاناً')}
          </span>
          <span className="px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-green-200 dark:border-green-700 font-bold text-green-700 dark:text-green-300">
            💵 {t('Retirer en argent', 'سحبها نقداً')} → {t('demande envoyée à l\'administrateur', 'يُرسل الطلب إلى المدير')}
          </span>
        </div>
      </div>

      {/* Pending rewards — the referrer chooses days OR cash */}
      {pendingRewards.length > 0 && (
        <div className={`rounded-2xl border p-4 mb-5 ${isDark ? 'bg-gray-800 border-amber-700' : 'bg-amber-50 border-amber-200'}`}>
          <h3 className={`font-black text-sm mb-3 ${txt}`}>🎉 {t('Récompenses à réclamer', 'مكافآت للمطالبة')} ({pendingRewards.length})</h3>
          <div className="space-y-2">
            {pendingRewards.map((rw) => (
              <div key={rw.referredUid} className={`rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
                <div className="text-sm">
                  <div className={`font-bold ${txt}`}>🎁 {rw.referredName}</div>
                  <div className={`text-xs ${sub}`}>{t('Choisissez votre récompense', 'اختر مكافأتك')}: <b>{rw.freeDays} {t('jours', 'يوم')}</b> {t('ou', 'أو')} <b>{rw.cashAmount} {rw.currency || 'DH'}</b></div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => redeem(rw.referredUid, 'days')}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white">
                    🎁 {rw.freeDays} {t('jours', 'يوم')}
                  </button>
                  <button onClick={() => redeem(rw.referredUid, 'cash')}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold bg-green-600 hover:bg-green-700 text-white">
                    💵 {rw.cashAmount} {rw.currency || 'DH'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {rpcError && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 mb-4 text-xs text-amber-700 dark:text-amber-300">
          ⚠️ {t('La liste détaillée des filleuls nécessite l\'activation d\'une fonction SQL (get_my_referrals). Vos gains ci-dessous restent corrects.',
                'قائمة المُحالين التفصيلية تتطلب تفعيل دالة SQL. أرباحك أدناه صحيحة.')}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[
          ['👥', t('Agences parrainées', 'الوكالات المُحالة'), referredCount, '#6366f1'],
          ['💰', t('Commissions gagnées', 'العمولات المكتسبة'), `${totalCommission} ${currency}`, '#10b981'],
          ['⏳', t('En attente (à réclamer)', 'قيد الانتظار'), `${pendingValue} ${currency}`, '#f59e0b'],
          ['🎁', t('Jours gratuits obtenus', 'أيام مجانية'), stats.freeDays, '#ec4899'],
        ].map(([icon, label, value, color]) => (
          <div key={label} className={`rounded-2xl border p-4 ${cardBg}`}>
            <div className="flex items-center justify-between">
              <span className="text-2xl">{icon}</span>
              <span className="text-2xl font-black" style={{ color }}>{value}</span>
            </div>
            <div className={`text-[11px] font-bold mt-1 ${sub}`}>{label}</div>
          </div>
        ))}
      </div>

      <div className={`rounded-2xl border p-4 mb-5 ${cardBg}`}>
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <h3 className={`font-black text-sm ${txt}`}>📋 {t('Utilisateurs parrainés', 'المستخدمون المُحالون')}</h3>
          <div className="flex items-center gap-2 flex-wrap">
            {['all', 'pending', 'confirmed', 'active'].map(k => (
              <button key={k} onClick={() => setFilter(k)}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold ${filter === k ? 'bg-indigo-600 text-white' : (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600')}`}>
                {k === 'all' ? t('Tous', 'الكل') : k === 'pending' ? t('Attente', 'انتظار') : k === 'confirmed' ? t('Confirmé', 'مؤكد') : t('Actif', 'نشط')}
              </button>
            ))}
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t('Rechercher…', 'بحث…')}
              className={`px-3 py-1 rounded-lg border text-xs ${isDark ? 'bg-gray-900 border-gray-600 text-gray-200' : 'bg-gray-50 border-gray-200'}`} />
          </div>
        </div>

        {loading ? (
          <div className={`text-center py-8 ${sub}`}>⏳</div>
        ) : filtered.length === 0 ? (
          <div className={`text-center py-8 text-sm ${sub}`}>
            📭 {t('Aucun parrainage pour le moment. Partagez votre lien !', 'لا توجد إحالات بعد. شارك رابطك!')}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className={sub}>
                    {[t('Nom', 'الاسم'), t('Email', 'البريد'), t('Inscription', 'التسجيل'), t('Statut', 'الحالة'), t('Jours', 'أيام')].map(h => (
                      <th key={h} className="text-left font-bold py-2 px-2">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r) => (
                    <tr key={r.uid} className={`border-t ${isDark ? 'border-gray-700 text-gray-200' : 'border-gray-100 text-gray-700'}`}>
                      <td className="py-2 px-2 font-bold">{r.name || '—'}</td>
                      <td className="py-2 px-2">{r.email || '—'}</td>
                      <td className="py-2 px-2 whitespace-nowrap">{r.createdAt ? new Date(r.createdAt).toLocaleDateString(isAr ? 'ar-MA' : 'fr-FR') : '—'}</td>
                      <td className="py-2 px-2">{statusBadge(r.status)}</td>
                      <td className="py-2 px-2 font-bold">{r.freeDays ? `+${r.freeDays}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pageCount > 1 && (
              <div className="flex items-center justify-center gap-2 mt-3">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className={`px-3 py-1 rounded-lg text-xs font-bold disabled:opacity-40 ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-600'}`}>‹</button>
                <span className={`text-xs ${sub}`}>{page} / {pageCount}</span>
                <button disabled={page >= pageCount} onClick={() => setPage(p => p + 1)}
                  className={`px-3 py-1 rounded-lg text-xs font-bold disabled:opacity-40 ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-600'}`}>›</button>
              </div>
            )}
          </>
        )}
      </div>

      <div className={`rounded-2xl border p-4 ${cardBg}`}>
        <h3 className={`font-black text-sm mb-3 ${txt}`}>🏆 {t('Historique des commissions', 'سجل العمولات')}</h3>
        {rewards.length === 0 ? (
          <div className={`text-center py-6 text-sm ${sub}`}>
            {t('Aucune commission pour le moment. Partagez votre lien : chaque agence parrainée qui paie son 1er abonnement vous rapporte une commission.',
               'لا توجد عمولات بعد. شارك رابطك: كل وكالة مُحالة تدفع أول اشتراك تمنحك عمولة.')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className={sub}>
                  {[t('Date', 'التاريخ'), t('Compte parrainé', 'الحساب المُحال'), t('Montant', 'المبلغ'), t('Statut', 'الحالة')].map(h => (
                    <th key={h} className="text-left font-bold py-2 px-2">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...rewards].reverse().map((rw, i) => {
                  const date = rw.redeemedAt || rw.grantedAt;
                  const statusInfo = !rw.redeemed
                    ? ['bg-amber-100 text-amber-700', t('En attente', 'قيد الانتظار')]
                    : rw.redeemType === 'cash'
                      ? ['bg-green-100 text-green-700', t('Retiré (argent)', 'سُحب نقداً')]
                      : ['bg-indigo-100 text-indigo-700', t('Converti en jours', 'حُوّل لأيام')];
                  return (
                    <tr key={i} className={`border-t ${isDark ? 'border-gray-700 text-gray-200' : 'border-gray-100 text-gray-700'}`}>
                      <td className="py-2 px-2 whitespace-nowrap">{date ? new Date(date).toLocaleDateString(isAr ? 'ar-MA' : 'fr-FR') : '—'}</td>
                      <td className="py-2 px-2 font-bold">{rw.referredName || rw.referredUid}</td>
                      <td className="py-2 px-2 font-bold">{rw.cashAmount} {rw.currency || 'DH'} <span className={sub}>/ {rw.freeDays} {t('j', 'ي')}</span></td>
                      <td className="py-2 px-2"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${statusInfo[0]}`}>{statusInfo[1]}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[2100] bg-gray-900 text-white px-5 py-2.5 rounded-xl text-sm font-bold shadow-lg">{toast}</div>
      )}
    </div>
  );
}
